<script setup lang="ts" generic="T extends EnumerationOptionDto | GeometryLabelDto">
import { type EnumerationOptionDto, type GeometryLabelDto } from '@/api/generator';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import VeeTextField from '@/components/forms/TextField/VeeTextField.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { type TranslatableEntity, translateEntityProperty } from '@/utils/helper.ts';
import { mdiPlus } from '@mdi/js';
import { useFieldArray, useValidateForm } from 'vee-validate';
import { toRef } from 'vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import { useVeeFormProps } from '../utils';

interface VeeInputRowsProps {
  name: string;
  deleteTooltipText?: string;
  disabledTooltipText?: string;
  addTooltipText: string;
  disabled?: boolean;
  isEditable?: boolean;
  isDeletable?: boolean;
  isExtendable?: boolean;
}

const props = withDefaults(defineProps<VeeInputRowsProps>(), {
  editMode: 'allowAll',
  deleteTooltipText: undefined,
  disabledTooltipText: undefined,
});

const veeProps = useVeeFormProps(props);

const validateForm = useValidateForm();

function newOption(): any {
  return { id: 0 };
}

function getTooltipForField(id: number) {
  if ((id === 0 || props.isDeletable) && props.deleteTooltipText) return props.deleteTooltipText;

  if (!props.isDeletable && props.disabledTooltipText) return props.disabledTooltipText;
}

const { fields, push, remove } = useFieldArray<T>(toRef(() => props.name));

const isNameInputFieldDisabled = (id?: number) => {
  if (veeProps.value.disabled) return true;
  if (id === 0) return false;

  return !props.isEditable;
};

const isDeleteButtonDisabled = (id?: number) => {
  if (veeProps.value.disabled) return true;

  // New/unsaved items are always deletable.
  if (id === 0) return false;

  return !props.isDeletable;
};
</script>

<template>
  <div>
    <InlineValidationError :name="name" />
    <v-list class="bg-transparent">
      <!-- list items -->
      <v-list-item v-for="(field, index) in fields" :key="field.key" class="pl-0 ml-0">
        <div class="d-flex">
          <VeeTextField
            :disabled="isNameInputFieldDisabled(field.value.id || 0)"
            :name="`${name}[${index}].name`"
            :data-testid="`inputRow_${index}`"
            :placeholder="$t('Required')"
            class="pl-0 w-100"
            :outputConverter="
              () => translateEntityProperty('name', field.value as TranslatableEntity)
            "
          >
          </VeeTextField>

          <Tooltip
            v-if="veeProps.mode !== 'details'"
            :disabled="!getTooltipForField(field.value.id || 0)"
          >
            <template #target="{ props: tooltipProps }">
              <div v-bind="tooltipProps" style="cursor: pointer">
                <PtwDeleteButton
                  :disabled="isDeleteButtonDisabled(field.value.id || 0)"
                  :class="{
                    'v-btn--disabled': isDeleteButtonDisabled(field.value.id || 0),
                  }"
                  data-testid="inputRowItem_removeBtn"
                  size="default"
                  @delete="
                    () => {
                      remove(index);
                      validateForm();
                    }
                  "
                />
              </div>
            </template>
            <template #tooltip>
              {{ getTooltipForField(field.value.id || 0) }}
            </template>
          </Tooltip>
        </div>
      </v-list-item>

      <p v-if="fields.length === 0 && veeProps.mode === 'details'">
        {{ EMPTY_FIELD_SIGN }}
      </p>
    </v-list>

    <Tooltip :disabled="!addTooltipText">
      <template #target="{ props: tooltipProps }">
        <TextVBtn
          v-if="veeProps.mode !== 'details'"
          v-bind="tooltipProps"
          data-testid="inputRowItem_addBtn"
          :disabled="!isExtendable"
          size="small"
          class="add-button"
          :icon="mdiPlus"
          variant="outlined"
          aria-label="Add"
          @click="push(newOption())"
        />
      </template>
      <template #tooltip>
        {{ addTooltipText }}
      </template>
    </Tooltip>
  </div>
</template>

<style lang="scss" scoped>
:deep(.invisible) {
  visibility: hidden;
}
.add-button {
  border: 2px solid;
}
</style>
